import { GoogleGenerativeAI } from "@google/generative-ai";


const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

if (!apiKey) {
    console.error(
        "REACT_APP_GEMINI_API_KEY is missing from .env"
    );
}

const genAI = new GoogleGenerativeAI(apiKey);

// Retry helper
const generateWithRetry = async (
    model,
    prompt,
    retries = 3
) => {
    for (let i = 0; i < retries; i++) {
        try {
            return await model.generateContent(prompt);
        } catch (error) {
            console.error(
                `Gemini attempt ${i + 1} failed:`,
                error
            );

            // Retry only on 503 errors
            if (
                error.message?.includes("503") &&
                i < retries - 1
            ) {
                await new Promise((resolve) =>
                    setTimeout(resolve, 3000)
                );

                continue;
            }

            throw error;
        }
    }
};

export const generateCoverIdeas = async ({
    title,
    author,
    genre,
    description,
}) => {
    try {
        console.log("Generating cover ideas...");

        const model = genAI.getGenerativeModel({
            model: "gemini-2.5-flash",
        });

        const prompt = `
Generate 5 unique professional book cover concepts.

Book Title: ${title}
Author: ${author}
Genre: ${genre}
Description: ${description}

Return ONLY valid JSON.

Example:

[
  {
    "title": "Dark Empire",
    "theme": "Gangster Drama",
    "colors": "Black, Gold, Red",
    "imageIdea": "A shadowy gangster standing before a city skyline"
  }
]
`;

        const result = await generateWithRetry(
            model,
            prompt,
            3
        );

        let responseText = result.response.text();

        responseText = responseText
            .replace(/```json/g, "")
            .replace(/```/g, "")
            .trim();

        try {
            return JSON.parse(responseText);
        } catch (parseError) {
            console.error(
                "JSON Parse Error, using fallback concepts:",
                parseError
            );
            return getFallbackConcepts(title, genre);
        }
    } catch (error) {
        console.error(
            "Error generating cover ideas, using fallback concepts:",
            error
        );
        return getFallbackConcepts(title, genre);
    }
};

const getFallbackConcepts = (title, genre) => {
    const defaultGenre = genre || "Fiction";
    return [
        {
            theme: `${defaultGenre} - Minimalist Modern`,
            colors: "Deep Navy, Matte White, Soft Gold",
            imageIdea: `A sleek, abstract geometric representation inspired by "${title}" with clean typography.`
        },
        {
            theme: `${defaultGenre} - Dramatic & Cinematic`,
            colors: "Charcoal Black, Crimson Red, Amber Glow",
            imageIdea: `A high-contrast silhouette of a central figure or object against an atmospheric light source.`
        },
        {
            theme: `${defaultGenre} - Vintage & Ornate`,
            colors: "Warm Sepia, Sage Green, Ivory Cream",
            imageIdea: `An elegant frame with intricate floral borders and classical serif lettering.`
        },
        {
            theme: `${defaultGenre} - Vibrant Retro`,
            colors: "Teal Blue, Mustard Yellow, Coral Pink",
            imageIdea: `A bold, flat-vector graphic illustration with retro textures inspired by "${title}".`
        },
        {
            theme: `${defaultGenre} - Dark Mood`,
            colors: "Midnight Black, Silver Grey, Cold Blue",
            imageIdea: `An atmospheric, misty landscape with a single point of light shining in the distance.`
        }
    ];
};